import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

/**
 * Compact relative time formatter.
 * Output format: Just now · 5 min · 10 min · 2 hr · 1 day · 1 mon · 1 yr
 */
const formatCompactTime = (timestamp, isOnline) => {
  if (!timestamp) return isOnline ? 'Just now' : '-';

  const date = new Date(timestamp);
  const time = date.getTime();
  if (isNaN(time)) return isOnline ? 'Just now' : '-';

  const diffMs = Date.now() - time;
  if (diffMs < 45000) return 'Just now';

  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min`;

  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hr`;

  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay} day`;

  const diffMon = Math.floor(diffDay / 30.4);
  if (diffMon < 12) return `${Math.max(1, diffMon)} mon`;

  const diffYr = Math.floor(diffDay / 365);
  return `${Math.max(1, diffYr)} yr`;
};

/**
 * DeviceOverviewTable
 * Enterprise Industrial IoT Device Fleet Telemetry Table.
 * Uses strictly live device records with real search, sorting, filtering, and pagination.
 */
const DeviceOverviewTable = ({
  devices = [],
  loading = false,
  selectedLocation = 'All Locations',
  onSelectDevice,
}) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [selectedIds, setSelectedIds] = useState([]);
  const [sortField, setSortField] = useState('status');
  const [sortAsc, setSortAsc] = useState(false);

  // Filter devices based on search, status tab, and location filter
  const filtered = useMemo(() => {
    return (devices || []).filter((d) => {
      if (!d) return false;

      // Location / Dealer filter
      if (selectedLocation && selectedLocation !== 'All Locations') {
        const city = d.owner?.city || '';
        const dealer = d.dealer?.name || '';
        if (city !== selectedLocation && dealer !== selectedLocation) {
          return false;
        }
      }

      // Status filter
      if (statusFilter === 'Online' && !d.isActive) return false;
      if (statusFilter === 'Offline' && d.isActive) return false;

      // Text Search
      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase();
        const serial = (d.machSerial || '').toLowerCase();
        const id = (d.deviceId || '').toLowerCase();
        const owner = `${d.owner?.fName || ''} ${d.owner?.lName || ''}`.toLowerCase();
        const city = (d.owner?.city || '').toLowerCase();
        const dealer = (d.dealer?.name || '').toLowerCase();
        const type = (d.machinetype || '').toLowerCase();

        return (
          serial.includes(term) ||
          id.includes(term) ||
          owner.includes(term) ||
          city.includes(term) ||
          dealer.includes(term) ||
          type.includes(term)
        );
      }

      return true;
    });
  }, [devices, searchTerm, statusFilter, selectedLocation]);

  // Sort filtered devices
  const sorted = useMemo(() => {
    const list = [...filtered];
    list.sort((a, b) => {
      let aVal = '';
      let bVal = '';

      if (sortField === 'deviceId') {
        aVal = a.deviceId || a.machSerial || '';
        bVal = b.deviceId || b.machSerial || '';
      } else if (sortField === 'cuts') {
        aVal = Number(a.data?.keycount) || 0;
        bVal = Number(b.data?.keycount) || 0;
        return sortAsc ? aVal - bVal : bVal - aVal;
      } else if (sortField === 'status') {
        aVal = a.isActive ? 1 : 0;
        bVal = b.isActive ? 1 : 0;
        return sortAsc ? aVal - bVal : bVal - aVal;
      } else if (sortField === 'lastActive') {
        aVal = a.lastActive ? new Date(a.lastActive).getTime() : 0;
        bVal = b.lastActive ? new Date(b.lastActive).getTime() : 0;
        return sortAsc ? aVal - bVal : bVal - aVal;
      }

      return sortAsc
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
    return list;
  }, [filtered, sortField, sortAsc]);

  // Pagination calculations
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const paginated = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, currentPage, pageSize]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  const toggleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(paginated.map((d) => d._id || d.machSerial));
    } else {
      setSelectedIds([]);
    }
  };

  const toggleSelectRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleDeviceClick = (machSerial) => {
    if (onSelectDevice) {
      onSelectDevice(machSerial);
    } else if (machSerial) {
      navigate(`/home/device/${machSerial}`);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200/90 shadow-xs overflow-hidden flex flex-col h-full">
      {/* Table Header Controls */}
      <div className="p-3.5 sm:p-4 border-b border-slate-200/80 flex flex-col lg:flex-row lg:items-center justify-between gap-3 bg-white">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-slate-900 text-sm tracking-tight">
              Connected Devices
            </h3>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-mono">
              {filtered.length}
            </span>
          </div>

          {/* Status Filter Tabs */}
          <div className="flex items-center bg-slate-100/90 p-0.5 rounded-lg border border-slate-200/60 text-xs">
            {['All', 'Online', 'Offline'].map((st) => {
              const active = statusFilter === st;
              return (
                <button
                  key={st}
                  onClick={() => {
                    setStatusFilter(st);
                    setCurrentPage(1);
                  }}
                  className={`px-2.5 py-1 rounded-md font-medium transition-all ${
                    active
                      ? 'bg-white text-slate-800 shadow-xs font-semibold'
                      : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  {st}
                </button>
              );
            })}
          </div>
        </div>

        {/* Search Input & Page Size */}
        <div className="flex items-center gap-2.5">
          <div className="relative w-full sm:w-64">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Filter by ID, serial, location..."
              className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-8 pr-3 py-1.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
            />
            <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none text-slate-400">
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </div>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute inset-y-0 right-0 pr-2.5 flex items-center text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            )}
          </div>

          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="border border-slate-200 bg-white text-slate-600 text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
          >
            <option value={5}>5 / page</option>
            <option value={10}>10 / page</option>
            <option value={25}>25 / page</option>
            <option value={50}>50 / page</option>
          </select>
        </div>
      </div>

      {/* Table Content */}
      <div className="overflow-x-auto flex-1 custom-scrollbar">
        <table className="w-full text-left text-xs border-collapse border border-slate-200">
          <thead>
            <tr className="bg-slate-50/80 text-slate-600 font-semibold">
              <th className="py-2.5 px-3 w-8 border border-slate-200">
                <input
                  type="checkbox"
                  onChange={toggleSelectAll}
                  checked={
                    paginated.length > 0 &&
                    paginated.every((d) => selectedIds.includes(d._id || d.machSerial))
                  }
                  className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5 cursor-pointer"
                />
              </th>
              <th
                onClick={() => handleSort('deviceId')}
                className="py-2.5 px-3 font-semibold text-slate-700 cursor-pointer select-none hover:text-blue-600 border border-slate-200"
              >
                <div className="flex items-center gap-1">
                  <span>Device Identifier</span>
                  {sortField === 'deviceId' && (sortAsc ? '↑' : '↓')}
                </div>
              </th>
              <th className="py-2.5 px-3 font-semibold text-slate-700 border border-slate-200">Model</th>
              <th className="py-2.5 px-3 font-semibold text-slate-700 border border-slate-200">Facility / Dealer</th>
              <th
                onClick={() => handleSort('status')}
                className="py-2.5 px-3 font-semibold text-slate-700 cursor-pointer select-none hover:text-blue-600 border border-slate-200"
              >
                <div className="flex items-center gap-1">
                  <span>Connectivity</span>
                  {sortField === 'status' && (sortAsc ? '↑' : '↓')}
                </div>
              </th>
              <th
                onClick={() => handleSort('cuts')}
                className="py-2.5 px-3 font-semibold text-slate-700 text-right cursor-pointer select-none hover:text-blue-600 border border-slate-200"
              >
                <div className="flex items-center justify-end gap-1">
                  <span>Key Cuts</span>
                  {sortField === 'cuts' && (sortAsc ? '↑' : '↓')}
                </div>
              </th>
              <th
                onClick={() => handleSort('lastActive')}
                className="py-2.5 px-3 font-semibold text-slate-700 text-right cursor-pointer select-none hover:text-blue-600 border border-slate-200"
              >
                <div className="flex items-center justify-end gap-1">
                  <span>Last Signal</span>
                  {sortField === 'lastActive' && (sortAsc ? '↑' : '↓')}
                </div>
              </th>
              <th className="py-2.5 px-3 text-center border border-slate-200">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-slate-700">
            {/* Loading Skeleton */}
            {loading && paginated.length === 0 && (
              <>
                {[1, 2, 3, 4, 5].map((i) => (
                  <tr key={i} className="animate-pulse">
                    <td className="py-3 px-3"><div className="w-3.5 h-3.5 bg-slate-200 rounded"></div></td>
                    <td className="py-3 px-3"><div className="w-24 h-4 bg-slate-200 rounded"></div></td>
                    <td className="py-3 px-3"><div className="w-16 h-4 bg-slate-100 rounded"></div></td>
                    <td className="py-3 px-3"><div className="w-28 h-4 bg-slate-100 rounded"></div></td>
                    <td className="py-3 px-3"><div className="w-16 h-5 bg-slate-200 rounded-full"></div></td>
                    <td className="py-3 px-3 text-right"><div className="w-12 h-4 bg-slate-200 rounded ml-auto"></div></td>
                    <td className="py-3 px-3 text-right"><div className="w-16 h-4 bg-slate-100 rounded ml-auto"></div></td>
                    <td className="py-3 px-3 text-center"><div className="w-12 h-5 bg-slate-200 rounded mx-auto"></div></td>
                  </tr>
                ))}
              </>
            )}

            {/* Empty State */}
            {!loading && paginated.length === 0 && (
              <tr>
                <td colSpan={8} className="py-12 text-center text-slate-500">
                  <div className="flex flex-col items-center justify-center max-w-sm mx-auto">
                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 mb-2.5">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <span className="font-semibold text-slate-700 text-sm">No machines found</span>
                    <span className="text-xs text-slate-400 mt-1">
                      {searchTerm
                        ? `No devices matched "${searchTerm}". Try adjusting your search or filters.`
                        : 'No IoT device records are registered on this account.'}
                    </span>
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm('')}
                        className="mt-3 text-xs font-semibold text-blue-600 hover:text-blue-700 underline"
                      >
                        Clear Search
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            )}

            {/* Real Device Rows */}
            {paginated.map((device) => {
              const rowId = device._id || device.machSerial;
              const isSelected = selectedIds.includes(rowId);
              const isOnline = Boolean(device.isActive);
              const cuts = device.data?.keycount;
              const locationText = device.owner?.city || device.dealer?.name || '-';

              return (
                <tr
                  key={rowId}
                  className={`hover:bg-slate-50/80 transition-colors ${
                    isSelected ? 'bg-blue-50/50' : ''
                  }`}
                >
                  <td className="py-2.5 px-3 border border-slate-200">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelectRow(rowId)}
                      className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5 cursor-pointer"
                    />
                  </td>

                  {/* Device Identifier */}
                  <td className="py-2.5 px-3 border border-slate-200">
                    <div
                      onClick={() => handleDeviceClick(device.machSerial)}
                      className="cursor-pointer group inline-block"
                    >
                      <div className="font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">
                        {device.deviceId || device.machSerial}
                      </div>
                      {device.deviceId && device.machSerial && (
                        <div className="text-[10px] font-mono text-slate-400">
                          {device.machSerial}
                        </div>
                      )}
                    </div>
                  </td>

                  {/* Machine Model */}
                  <td className="py-2.5 px-3 text-slate-600 font-medium border border-slate-200">
                    {device.machinetype || 'DUO IQ'}
                  </td>

                  {/* Facility / Dealer */}
                  <td className="py-2.5 px-3 text-slate-600 border border-slate-200">
                    <span className="truncate max-w-[160px] inline-block font-normal" title={locationText}>
                      {locationText}
                    </span>
                  </td>

                  {/* Connectivity Status */}
                  <td className="py-2.5 px-3 border border-slate-200">
                    {isOnline ? (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                        </span>
                        Online
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-600 border border-slate-200">
                        <span className="h-1.5 w-1.5 rounded-full bg-slate-400"></span>
                        Offline
                      </span>
                    )}
                  </td>

                  {/* Total Cuts */}
                  <td className="py-2.5 px-3 text-right font-mono font-semibold text-slate-800 border border-slate-200">
                    {cuts != null ? Number(cuts).toLocaleString() : '-'}
                  </td>

                  {/* Last Signal */}
                  <td className="py-2.5 px-3 text-right text-slate-500 font-mono text-[11px] whitespace-nowrap border border-slate-200">
                    <span title={device.lastActive ? moment(device.lastActive).format('DD MMM YYYY, HH:mm:ss') : undefined}>
                      {formatCompactTime(device.lastActive, isOnline)}
                    </span>
                  </td>

                  {/* Action */}
                  <td className="py-2.5 px-3 text-center border border-slate-200">
                    <button
                      onClick={() => handleDeviceClick(device.machSerial)}
                      className="p-1 rounded-md hover:bg-slate-100 text-slate-400 hover:text-blue-600 transition-colors"
                      title="Inspect Machine Telemetry"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      {sorted.length > 0 && (
        <div className="p-3 border-t border-slate-200 bg-white flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-slate-500">
          <div>
            Showing <span className="font-semibold text-slate-800">{(currentPage - 1) * pageSize + 1}</span> to{' '}
            <span className="font-semibold text-slate-800">
              {Math.min(currentPage * pageSize, sorted.length)}
            </span>{' '}
            of <span className="font-semibold text-slate-800">{sorted.length}</span> devices
          </div>

          <div className="flex items-center gap-1">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              className="px-2 py-1 flex items-center justify-center rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-40 font-medium transition-colors"
            >
              Prev
            </button>
            <span className="px-2 text-slate-600 font-medium">
              {currentPage} / {totalPages}
            </span>
            <button
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              className="px-2 py-1 flex items-center justify-center rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-40 font-medium transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceOverviewTable;
